import { useEffect } from 'react';
import { updateSEOMetadata, addStructuredData, removeStructuredData, generateOrganizationSchema, generateLocalBusinessSchema, generateWebSiteSchema, generatePropertySchema, generateBreadcrumbSchema, generateFAQSchema, generateReviewSchema } from '../utils/seo';

interface UseSEOMetaTagsProps {
  title: string;
  description: string;
  keywords?: string[];
  locale: string;
  path?: string;
  ogImage?: string;
  type?: 'website' | 'article';
  propertyData?: any;
  breadcrumbs?: { name: string; url: string }[];
  faqData?: Array<{question: string, answer: string}>;
  reviewData?: {
    reviewBody: string;
    reviewerName: string;
    ratingValue: number;
    datePublished: string;
  };
}

export const useSEOMetaTags = ({
  title,
  description,
  keywords = [],
  locale,
  path = '',
  ogImage = '/og-image.jpg',
  type = 'website',
  propertyData,
  breadcrumbs,
  faqData,
  reviewData
}: UseSEOMetaTagsProps) => {
  useEffect(() => {
    // Update SEO metadata
    updateSEOMetadata({
      title,
      description,
      keywords,
      locale: locale as any, // Type assertion since we're accepting string
      path,
      ogImage,
      type
    });

    // Add structured data based on page type
    // Organization schema (add once per site)
    const orgSchema = generateOrganizationSchema(locale as any);
    addStructuredData(orgSchema, 'organization-schema');

    // LocalBusiness schema (add once per site)
    const localBusinessSchema = generateLocalBusinessSchema(locale as any);
    addStructuredData(localBusinessSchema, 'local-business-schema');

    // Website schema (add once per site)
    const websiteSchema = generateWebSiteSchema(locale as any);
    addStructuredData(websiteSchema, 'website-schema');

    // Property schema if property data is provided
    if (propertyData) {
      const propertySchema = generatePropertySchema(propertyData, locale as any);
      addStructuredData(propertySchema, 'property-schema');
    }

    // Breadcrumb schema if breadcrumbs are provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
      addStructuredData(breadcrumbSchema, 'breadcrumb-schema');
    }

    // FAQ schema if FAQ data is provided
    if (faqData && faqData.length > 0) {
      const faqSchema = generateFAQSchema(faqData, locale as any);
      addStructuredData(faqSchema, 'faq-schema');
    }

    // Review schema if review data is provided
    if (reviewData) {
      const reviewSchema = generateReviewSchema(reviewData, locale as any);
      addStructuredData(reviewSchema, 'review-schema');
    }

    // Cleanup function to remove structured data when component unmounts
    return () => {
      removeStructuredData('organization-schema');
      removeStructuredData('local-business-schema');
      removeStructuredData('website-schema');
      removeStructuredData('property-schema');
      removeStructuredData('breadcrumb-schema');
      removeStructuredData('faq-schema');
      removeStructuredData('review-schema');
    };
  }, [title, description, keywords, locale, path, ogImage, type, propertyData, breadcrumbs, faqData, reviewData]);
};

export default useSEOMetaTags;
